import { Box, EmptyState, Text } from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDebounce } from 'react-use';

import {
  CustomFieldAttribute,
  CustomFieldSelectAttributeOption,
} from 'apis/customFields';
import { HookFormSelectionList } from 'components/hookForm';
import { isLeafNode } from 'utils/elevion';
import { convertTreeToFlatItems } from 'utils/tree';

import { AttributeFormFieldValues } from '../../../../../../../../../customAttributeModalForm.types';
import {
  renderItemOrList,
  SelectionListItemData,
} from '../../../renderItemOrList';

type CustomAttributeLeafValueFieldProps = {
  parentName: string;
  fieldName: string;
  customFields: CustomFieldAttribute[];
  disabled: boolean;
};

export const CustomAttributeLeafValueField = ({
  parentName,
  fieldName,
  customFields,
  disabled,
}: CustomAttributeLeafValueFieldProps): JSX.Element => {
  // TODO: [TEAMS-13693] remove omit after updating to RHF v8
  const { control } =
    useFormContext<Omit<AttributeFormFieldValues, 'options'>>();
  const node = useWatch({
    control,
    name: parentName as 'visibility_condition.nodes.0',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useDebounce(
    () => {
      return setDebouncedSearchTerm(searchTerm);
    },
    300,
    [searchTerm],
  );

  const mapCustomFieldOptions = (
    option: CustomFieldSelectAttributeOption,
  ): SelectionListItemData => {
    if (option.options && option.options.length > 0) {
      const value = option.value;
      const isSelected =
        isLeafNode(node) &&
        Array.isArray(node.value) &&
        node.value.includes(value);

      if (isSelected) {
        return {
          value: option.value,
          title: option.label,
        };
      }

      return {
        title: option.label,
        items: option.options.map(mapCustomFieldOptions),
        ...(option.value && { value: option.value }),
      };
    }
    return {
      value: option.value,
      title: option.label,
    };
  };

  const customField =
    customFields.find(
      (field) =>
        isLeafNode(node) && node.field === `custom__${field.identifier}`,
    ) || null;
  const customFieldOptions = useMemo(
    () => (customField && 'options' in customField ? customField.options : []),
    [customField],
  );
  const flatCustomFieldOptionsWithPath = useMemo(
    () =>
      convertTreeToFlatItems(customFieldOptions, {
        childrenKey: 'options',
        transformItem: (option, path): CustomFieldSelectAttributeOption => {
          return {
            ...option,
            label: path
              .map(({ label }) => label)
              .concat([option.label])
              .join(' / '),
          };
        },
      }).filter(({ options }) => !options?.length),
    [customFieldOptions],
  );

  const options = debouncedSearchTerm
    ? flatCustomFieldOptionsWithPath
    : customFieldOptions;

  const selectItems: SelectionListItemData[] = options
    .map((option) => mapCustomFieldOptions(option))
    .filter((item) => item.value);

  const filteredSelectItems = useMemo(() => {
    if (!debouncedSearchTerm) {
      return selectItems;
    }

    const fuse = new Fuse(selectItems, {
      keys: ['title'],
      threshold: 0.2,
      distance: 200,
    });

    return fuse.search(debouncedSearchTerm).map(({ item }) => item);
  }, [debouncedSearchTerm, selectItems]);

  const inputSelectionListProps = useMemo(
    () => ({
      multiple: true,
      getItemTitleByValue: (value: string | null | undefined) => {
        return (
          <Text.Paragraph>
            {value ? getAttributeLabelByValue(value, customFieldOptions) : null}
          </Text.Paragraph>
        );
      },
      placeholder: 'Select a value',
      selectionListProps: {},
      dropdownProps: {
        style: {
          width: 'auto',
          minWidth: '280px',
        },
      },
      filter: {
        value: searchTerm,
        onChange: (event) => setSearchTerm(event.target.value),
      },
    }),
    [customFieldOptions, searchTerm],
  );

  return (
    <HookFormSelectionList
      name={fieldName as 'visibility_condition.nodes.0.value'}
      control={control}
      noLabel
      noAlert
      disabled={disabled}
      componentProps={inputSelectionListProps}
    >
      {filteredSelectItems.map((item) =>
        renderItemOrList(item, debouncedSearchTerm),
      )}
      {debouncedSearchTerm && filteredSelectItems.length === 0 && (
        <Box as="li" padding="250 300">
          <EmptyState
            title="No results found"
            description="Try changing your search criteria."
          />
        </Box>
      )}
    </HookFormSelectionList>
  );
};

/** Creates a proper label containing path based on selected value. */
function getAttributeLabelByValue(
  value: string,
  options: readonly CustomFieldSelectAttributeOption[],
): string | null {
  const option = options.find((option) => option.value === value);

  if (option) {
    return option.label;
  }

  for (const option of options) {
    if (option.options) {
      const label = getAttributeLabelByValue(value, option.options);

      if (label) {
        return `${option.label} / ${label}`;
      }
    }
  }

  return null;
}
